/**
 * Spiris OAuth 2.0 client — shared by the web app's OAuth routes and the
 * runtime's token refresh.
 *
 * Spiris authenticates through Visma's identity server (IdentityServer), the
 * same one behind the Payroll API. One Squidler-owned client per environment:
 *   SPIRIS_OAUTH_CLIENT_ID
 *   SPIRIS_OAUTH_CLIENT_SECRET
 *   SPIRIS_OAUTH_REDIRECT_URI
 * A SANDBOX client cannot reach a production company (and a production client
 * can reach the sandbox), so dev runs on the sandbox client and prod needs a
 * production client issued after Spiris's partner review. Redirect URIs are
 * HTTPS only and registered by Spiris on request (api@spiris.se).
 *
 * Shaped after `../bokio/oauth.ts` — same env triple, same Basic auth, same
 * form-encoded token call. What differs, all verified against the sandbox on
 * 2026-10-22 rather than taken from the docs alone:
 *
 *  1. THE REFRESH TOKEN DOES NOT ROTATE. A refresh returns the SAME refresh
 *     token, and one already redeemed is redeemable again. So two concurrent
 *     refreshes are a wasted call, not a destroyed connection, and the runtime
 *     needs no refresh lock (`tools/spiris/tokens.ts`). It still persists
 *     whatever comes back, which costs nothing and survives a future change.
 *
 *  2. IT LASTS TWO YEARS FROM ISSUE, and API use does not extend it — a refresh
 *     hands back the same token with the same clock. The docs also say a
 *     password change by the user invalidates it. There is nothing a sweeper
 *     could renew, so Spiris is not registered with one; a dead token reaches
 *     the owner through the shared reauth notice on its first refusal.
 *
 *  3. THE TOKEN RESPONSE DOES NOT NAME THE COMPANY, but the access token is a
 *     JWT whose `customer_id` claim does (the same value for every grant to
 *     that company), and whose `email` is the person who authorized. Read
 *     with `accessTokenClaims` — decoded, not verified, which is sound only
 *     because the token was handed to us by Visma's token endpoint over TLS a
 *     moment earlier. Never call it on a token from anywhere else.
 *
 *  4. THE COMPANY PICKER IS ASKED FOR. `prompt=select_account` makes the
 *     identity server show its company selector instead of silently using the
 *     user's default company — which for a bureau is the wrong one more often
 *     than not. `acr_values` narrows that list to companies that have
 *     Bokföring & Fakturering at all.
 *
 *  5. TWO GRANTS COEXIST. Authorizing the same company again, for a second
 *     agent, leaves the first grant working — unlike Fortnox's service
 *     accounts. Nothing here has to protect against it.
 */

import { normalizeSpirisCompanyId } from "../integrations/connections";

const IDENTITY_BASE = "https://identity.vismaonline.com/connect";
const AUTH_URL = `${IDENTITY_BASE}/authorize`;
const TOKEN_URL = `${IDENTITY_BASE}/token`;
const REVOCATION_URL = `${IDENTITY_BASE}/revocation`;

/** Limits Visma's company picker to companies with Bokföring & Fakturering. */
const BOOKKEEPING_SERVICE_ACR = "service:44643EB1-3F76-4C1C-A672-402AE8085934";

interface OAuthConfig {
  readonly clientId: string;
  readonly clientSecret: string;
  readonly redirectUri: string;
}

const readConfig = (): OAuthConfig | null => {
  const clientId = process.env.SPIRIS_OAUTH_CLIENT_ID;
  const clientSecret = process.env.SPIRIS_OAUTH_CLIENT_SECRET;
  const redirectUri = process.env.SPIRIS_OAUTH_REDIRECT_URI;
  if (!clientId || !clientSecret || !redirectUri) return null;
  return { clientId, clientSecret, redirectUri };
};

const getConfig = (): OAuthConfig => {
  const config = readConfig();
  if (!config) {
    throw new Error(
      "Spiris OAuth not configured — set SPIRIS_OAUTH_CLIENT_ID, " +
      "SPIRIS_OAUTH_CLIENT_SECRET, and SPIRIS_OAUTH_REDIRECT_URI",
    );
  }
  return config;
};

export const isSpirisConfigured = (): boolean => readConfig() !== null;

// --- Auth URL ---

interface BuildAuthUrlArgs {
  readonly scopes: readonly string[];
  readonly state: string;
}

/**
 * Build the consent URL. `state` is persisted server-side in `oauth_states`,
 * bound to the initiating user and instance, and consumed single-use on
 * callback.
 *
 * There is no parameter that pre-selects a company the way Bokio's
 * `bokio_tenantid` does, so re-authorizing a connection shows the picker again
 * and the callback checks the company that came back is the one being
 * re-authorized (`oauth-company-match`).
 */
export const buildAuthUrl = ({ scopes, state }: BuildAuthUrlArgs): string => {
  const { clientId, redirectUri } = getConfig();
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: scopes.join(" "),
    state,
    prompt: "select_account",
    acr_values: BOOKKEEPING_SERVICE_ACR,
  });
  return `${AUTH_URL}?${params.toString()}`;
};

// --- Token exchange ---

export interface SpirisTokenResponse {
  readonly access_token: string;
  readonly refresh_token: string;
  readonly expires_in: number;
  readonly token_type: string;
  /** Space-separated, exactly what was granted. */
  readonly scope: string;
}

export class SpirisOAuthError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = "SpirisOAuthError";
  }
}

/**
 * A spent or revoked grant: no retry helps, the browser flow must run again.
 *
 * Only `invalid_grant`. `invalid_client` means OUR credentials are wrong — a
 * deploy problem that would otherwise flip every connection in the fleet to
 * needs-reauth and send every owner through a consent screen to fix a bug of
 * ours. It is reported loudly and retried, never blamed on the customer.
 */
export const isUnrecoverable = (err: unknown): boolean =>
  err instanceof SpirisOAuthError && err.code === "invalid_grant";

const basicAuth = (clientId: string, clientSecret: string): string =>
  `Basic ${Buffer.from(`${clientId}:${clientSecret}`).toString("base64")}`;

const postToken = async (body: URLSearchParams): Promise<SpirisTokenResponse> => {
  const { clientId, clientSecret } = getConfig();
  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
      Authorization: basicAuth(clientId, clientSecret),
    },
    body,
  });
  const parsed: unknown = await res.json().catch(() => ({}));
  const payload = (parsed ?? {}) as Record<string, unknown>;
  if (!res.ok) {
    const code = typeof payload.error === "string" ? payload.error : `http_${res.status}`;
    const description = typeof payload.error_description === "string"
      ? payload.error_description
      : `HTTP ${res.status}`;
    throw new SpirisOAuthError(code, description);
  }
  if (typeof payload.access_token !== "string" || typeof payload.refresh_token !== "string") {
    // A 200 without both tokens must never be written over the only live
    // credential we hold.
    throw new SpirisOAuthError(
      "malformed_token_response",
      "token response missing access_token or refresh_token",
    );
  }
  return {
    access_token: payload.access_token,
    refresh_token: payload.refresh_token,
    expires_in: typeof payload.expires_in === "number" ? payload.expires_in : 3600,
    token_type: typeof payload.token_type === "string" ? payload.token_type : "Bearer",
    scope: typeof payload.scope === "string" ? payload.scope : "",
  };
};

/** Exchange an authorization code for tokens. The code lives five minutes. */
export const exchangeCode = (code: string): Promise<SpirisTokenResponse> => {
  const { redirectUri } = getConfig();
  return postToken(new URLSearchParams({
    grant_type: "authorization_code",
    code,
    // Compared byte for byte against the authorize request.
    redirect_uri: redirectUri,
  }));
};

/** Redeem the refresh token. Persist what comes back all the same (see 1. above). */
export const refreshAccessToken = (refreshToken: string): Promise<SpirisTokenResponse> =>
  postToken(new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
  }));

/**
 * Revoke a refresh token at disconnect, so the grant does not outlive the
 * connection on Spiris's side. Throws when Visma does not confirm it, like the
 * Google and Microsoft revokes; the caller treats revocation as best effort and
 * deletes the row regardless, because a disconnect the user asked for must not
 * hang on Visma's identity server.
 */
export const revokeRefreshToken = async (refreshToken: string): Promise<void> => {
  const { clientId, clientSecret } = getConfig();
  const res = await fetch(REVOCATION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: basicAuth(clientId, clientSecret),
    },
    body: new URLSearchParams({ token: refreshToken, token_type_hint: "refresh_token" }),
  });
  if (!res.ok) throw new SpirisOAuthError(`http_${res.status}`, "spiris did not confirm the revocation");
};

// --- What the access token says ---

export interface SpirisTokenClaims {
  /** The company — the same for every grant to it. Our `tenant_key`. */
  readonly customerId: string;
  /** Who authorized. The grant is theirs: it dies when they change password. */
  readonly email: string | null;
}

/**
 * The company and the authorizing user, from the access token's JWT payload.
 *
 * Decoded without verifying the signature, which is sound ONLY for a token we
 * just received from Visma's token endpoint ourselves (see 3. above). Throws
 * rather than guessing when the claim is missing: a connection keyed on the
 * wrong company is the failure this integration must not have.
 */
export const accessTokenClaims = (accessToken: string): SpirisTokenClaims => {
  const payloadPart = accessToken.split(".")[1];
  if (payloadPart === undefined) throw new SpirisOAuthError("malformed_access_token", "access token is not a JWT");
  const decoded: unknown = JSON.parse(Buffer.from(payloadPart, "base64url").toString("utf8"));
  const claims = (typeof decoded === "object" && decoded !== null ? decoded : {}) as Record<string, unknown>;
  const customerId = claims.customer_id;
  if (typeof customerId !== "string" || customerId.trim() === "") {
    throw new SpirisOAuthError("missing_customer_id", "access token carries no customer_id claim");
  }
  return {
    customerId: normalizeSpirisCompanyId(customerId),
    email: typeof claims.email === "string" ? claims.email : null,
  };
};

/** The granted scope set, sorted — what `granted_scopes` stores. */
export const grantedScopesOf = (response: SpirisTokenResponse): readonly string[] =>
  [...new Set(response.scope.split(/\s+/).filter((s) => s.length > 0))].sort();
